import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import PlatformPermissionsChecklist from "@/components/PlatformPermissionsChecklist";
import { 
  Zap, 
  Settings, 
  Calendar, 
  Image as ImageIcon, 
  Video, 
  Check, 
  ChevronRight, 
  ChevronDown,
  Instagram,
  Facebook,
  Play,
  Pause,
  Info,
  AlertTriangle,
  Sparkles,
  CreditCard,
  RefreshCw,
  Clock,
  Globe,
  Target,
  ArrowLeft,
  Loader2,
  Plus,
  Tag,
  Building2,
  Users,
  Eye
} from "lucide-react";

// Platform icons mapping
const PlatformIcon = ({ platform, className = "w-4 h-4" }) => {
  const icons = {
    instagram: <Instagram className={className} />,
    facebook: <Facebook className={className} />,
    tiktok: <Play className={className} />,
    pinterest: <Target className={className} />
  };
  return icons[platform] || <Globe className={className} />;
};

export default function AutoPilotSetup({ onClose, onActivated }) {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Platform permissions tracking for TikTok/Pinterest
  const [platformPermissionsGranted, setPlatformPermissionsGranted] = useState({
    tiktok: null, // null = not checked yet, true = consented, false = needs consent
    pinterest: null
  });
  
  // Configuration state
  const [config, setConfig] = useState({
    enabled: true,
    approval_mode: "review_first",
    batch_frequency: "monthly",
    picture_posts_enabled: true,
    picture_posts_per_week: 3,
    video_posts_enabled: false,
    video_posts_per_week: 0,
    video_duration: 20,
    video_resolution: "720p",
    platforms: [],
    auto_renewal_enabled: false,
    auto_topup_enabled: false,
    auto_topup_max_amount: 25,
    image_source: "smart_mix", // "real_photos", "ai_generated", or "smart_mix"
    smart_image_mode: "auto" // "auto", "abstract", "lifestyle" - only applies to AI generated images
  });
  
  // Check if selected platforms need permissions
  const isTikTokSelected = config.platforms.includes("tiktok");
  const isPinterestSelected = config.platforms.includes("pinterest");
  
  const needsPlatformPermissions = () => {
    if (isTikTokSelected && platformPermissionsGranted.tiktok === false) return true;
    if (isPinterestSelected && platformPermissionsGranted.pinterest === false) return true;
    return false;
  };
  
  // Handler when permissions are completed
  const handlePlatformPermissionsComplete = (completedPlatforms) => {
    setPlatformPermissionsGranted(prev => {
      const updated = { ...prev };
      if (completedPlatforms.includes("tiktok")) updated.tiktok = true;
      if (completedPlatforms.includes("pinterest")) updated.pinterest = true;
      return updated;
    });
  };
  
  // Check platform consent status when platforms change
  useEffect(() => {
    const checkPlatformConsent = async () => {
      const updates = { ...platformPermissionsGranted };
      
      if (isTikTokSelected && platformPermissionsGranted.tiktok === null) {
        try {
          const res = await apiClient.get("/social/tiktok/auto-posting-settings");
          updates.tiktok = res.data?.auto_posting_consent === true;
        } catch (e) {
          updates.tiktok = false;
        }
      }
      
      if (isPinterestSelected && platformPermissionsGranted.pinterest === null) {
        try {
          const res = await apiClient.get("/social/pinterest/auto-posting-settings");
          updates.pinterest = res.data?.auto_posting_consent === true;
        } catch (e) {
          updates.pinterest = false;
        }
      }
      
      setPlatformPermissionsGranted(updates);
    };
    
    if (isTikTokSelected || isPinterestSelected) {
      checkPlatformConsent();
    }
  }, [isTikTokSelected, isPinterestSelected]);
  
  // Data fetched from API
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [creditEstimate, setCreditEstimate] = useState(null);
  const [contentPlan, setContentPlan] = useState(null);
  const [activeProfile, setActiveProfile] = useState(null);
  const [showFullYear, setShowFullYear] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [previewWeek, setPreviewWeek] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const navigate = useNavigate();
  
  // Preview mode for Real vs AI comparison
  const [previewMode, setPreviewMode] = useState("compare"); // "real", "ai", or "compare"
  
  // Frequency options
  const frequencyOptions = [
    { value: 1, label: "1/week" },
    { value: 2, label: "2/week" },
    { value: 3, label: "3/week" },
    { value: 5, label: "5/week" },
    { value: 7, label: "Daily" }
  ];
  
  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [accountsRes, profileRes] = await Promise.all([
          apiClient.get("/social/"),
          apiClient.get("/users/profiles/active/current").catch(() => null)
        ]);
        
        setSocialAccounts(accountsRes.data || []);
        if (profileRes?.data) {
          setActiveProfile(profileRes.data);
        }
        
        // Auto-select connected platforms
        const connectedPlatforms = (accountsRes.data || [])
          .filter(a => a.is_connected)
          .map(a => a.platform);
        
        if (connectedPlatforms.length > 0) {
          setConfig(prev => ({ ...prev, platforms: connectedPlatforms }));
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Calculate credits whenever config changes
  useEffect(() => {
    const calculateCredits = async () => {
      if (step !== 2) return;
      
      try {
        const res = await apiClient.post("/playbook/calculate-credits", {
          picture_posts_per_week: config.picture_posts_enabled ? config.picture_posts_per_week : 0,
          video_posts_per_week: config.video_posts_enabled ? config.video_posts_per_week : 0,
          video_duration: config.video_duration,
          video_resolution: config.video_resolution
        });
        setCreditEstimate(res.data);
      } catch (err) {
        console.error("Failed to calculate credits:", err);
      }
    };
    
    calculateCredits();
  }, [step, config.picture_posts_per_week, config.video_posts_per_week, config.video_duration, config.video_resolution, config.picture_posts_enabled, config.video_posts_enabled]);
  
  // Fetch content plan when reaching step 4
  useEffect(() => {
    const fetchContentPlan = async () => {
      if (step !== 4) return;
      
      try {
        const res = await apiClient.get("/playbook/content-plan");
        setContentPlan(res.data);
      } catch (err) {
        console.error("Failed to fetch content plan:", err);
      }
    };
    
    fetchContentPlan();
  }, [step]);
  
  // State for preview posts
  const [previewPosts, setPreviewPosts] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Fetch preview posts immediately when component mounts (for Step 1)
  useEffect(() => {
    const fetchPreviewPosts = async () => {
      setLoadingPreview(true);
      try {
        const res = await apiClient.get("/playbook/preview-posts");
        setPreviewPosts(res.data.preview_posts || []);
      } catch (err) {
        console.error("Failed to fetch preview posts:", err);
      } finally {
        setLoadingPreview(false);
      }
    };
    
    fetchPreviewPosts();
  }, []);
  
  // Toggle platform selection
  const togglePlatform = (platform) => {
    setConfig(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };
  
  // Generate text-only content preview for a week (NO image generation to avoid cost abuse)
  const generateTextPreview = async (week) => {
    setPreviewWeek(week);
    setLoadingPreview(true);
    try {
      // Generate preview content using AI without image generation
      const response = await apiClient.post("/playbook/text-preview", {
        theme: week.theme,
        content_angle: week.content_angle,
        events: week.events,
        industry: activeProfile?.industry || "general",
        brand_voice: activeProfile?.brand_voice || "",
        target_audience: activeProfile?.target_audience || ""
      });
      setPreviewContent(response.data);
    } catch (err) {
      // Generate fallback preview content if API fails
      setPreviewContent({
        caption: `✨ ${week.theme} - ${week.content_angle}\n\nThis week's focus brings you insights about ${week.theme.toLowerCase()}. Stay tuned for valuable content that resonates with your audience!\n\n${week.events?.length > 0 ? `🎉 Relevant: ${week.events.slice(0, 2).join(', ')}` : ''}\n\n#YourBrand #ContentMarketing`,
        hashtags: week.suggested_hashtags || ["#ContentMarketing", "#SocialMedia", "#Business"],
        cta: "Ready to engage? Like, comment, and share!",
        platform_tips: {
          instagram: "Best time to post: 11 AM - 1 PM",
          facebook: "Include a question to boost engagement",
          tiktok: "Keep it under 60 seconds for best reach",
          pinterest: "Use vertical images (2:3 ratio)"
        }
      });
    } finally {
      setLoadingPreview(false);
    }
  };
  
  // Apply suggested adjustment
  const applyAdjustment = (suggestion) => {
    if (suggestion.type === "picture_adjustment") {
      setConfig(prev => ({ ...prev, picture_posts_per_week: suggestion.adjusted_value }));
    } else if (suggestion.type === "video_quantity_adjustment") {
      setConfig(prev => ({ ...prev, video_posts_per_week: suggestion.adjusted_value }));
    } else if (suggestion.type === "video_resolution_adjustment") {
      setConfig(prev => ({ ...prev, video_resolution: suggestion.adjusted_value }));
    }
  };
  
  // Activate Auto-Pilot
  const activateAutoPilot = async () => {
    setSavingConfig(true);
    try {
      await apiClient.post("/playbook/config", config);
      toast.success("Auto-Pilot activated successfully!");
      if (onActivated) onActivated();
      if (onClose) onClose();
      refreshUser();
    } catch (err) {
      toast.error("Failed to activate Auto-Pilot");
      console.error(err);
    } finally {
      setSavingConfig(false);
    }
  };
  
  // Render Step 1: Brand Profile Display (Confidently show what we know)
  const renderStep1 = () => {
    const brandLearning = activeProfile?.brand_learning || {};
    const businessLocation = brandLearning.business_location || {};
    const brandVoice = brandLearning.brand_voice || "";
    const targetAudience = brandLearning.target_audience || "";
    const brandKeywords = brandLearning.brand_keywords || [];
    const brandColors = brandLearning.brand_colors || [];
    const companyDescription = brandLearning.company_description || activeProfile?.description || "";
    
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-viraly-orange to-orange-600 flex items-center justify-center mx-auto mb-4">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-viraly-light mb-2">Auto-Pilot Setup</h2>
          <p className="text-viraly-muted">
            Based on everything we've learned about <span className="text-viraly-orange font-semibold">{activeProfile?.name || "your brand"}</span>, 
            here's how we'll create content for you.
          </p>
        </div>
        
        {/* What We Know - Confidently Displayed */}
        <div className="glass-card p-6 rounded-xl border border-viraly-orange/30 bg-viraly-orange/5">
          <h3 className="text-lg font-semibold text-viraly-light mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-viraly-orange" />
            What We Know About Your Brand
          </h3>
          
          <div className="grid gap-4">
            {/* Profile Name & Industry */}
            <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
              <div className="w-10 h-10 rounded-lg bg-viraly-orange/20 flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-viraly-orange" />
              </div>
              <div>
                <p className="text-sm text-viraly-muted">Business Profile</p>
                <p className="text-viraly-light font-medium">{activeProfile?.name || "Your Business"}</p>
                <p className="text-sm text-viraly-orange capitalize">{activeProfile?.niche || "General Business"}</p>
              </div>
            </div>
            
            {/* Location */}
            {(businessLocation.city || businessLocation.country) && (
              <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-viraly-muted">Location & Timezone</p>
                  <p className="text-viraly-light font-medium">
                    {[businessLocation.city, businessLocation.country].filter(Boolean).join(", ")}
                  </p>
                  <p className="text-xs text-viraly-muted">Content will be optimized for local trends & events</p>
                </div>
              </div>
            )}
            
            {/* Brand Voice */}
            {brandVoice && (
              <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-viraly-muted">Brand Voice</p>
                  <p className="text-viraly-light font-medium capitalize">{brandVoice}</p>
                </div>
              </div>
            )}
            
            {/* Target Audience */}
            {targetAudience && (
              <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-viraly-muted">Target Audience</p>
                  <p className="text-viraly-light font-medium">{targetAudience}</p>
                </div>
              </div>
            )}
            
            {/* Brand Keywords */}
            {brandKeywords.length > 0 && (
              <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                  <Tag className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-sm text-viraly-muted">Brand Keywords</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {brandKeywords.slice(0, 5).map((keyword, i) => (
                      <span key={i} className="px-2 py-0.5 bg-white/10 text-viraly-light rounded text-xs">
                        {keyword}
                      </span>
                    ))}
                    {brandKeywords.length > 5 && (
                      <span className="px-2 py-0.5 bg-white/5 text-viraly-muted rounded text-xs">
                        +{brandKeywords.length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Company Description Summary */}
            {companyDescription && (
              <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                  <Info className="w-5 h-5 text-pink-400" />
                </div>
                <div>
                  <p className="text-sm text-viraly-muted">About Your Business</p>
                  <p className="text-viraly-light text-sm line-clamp-2">{companyDescription}</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
            <p className="text-xs text-viraly-muted">
              Using this intelligence to create relevant content
            </p>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-viraly-orange"
              onClick={() => window.location.href = "/brand"}
            >
              Update Profile
            </Button>
          </div>
        </div>
        
        {/* Profile Selector for Multi-Profile Users */}
        {user?.profiles && user.profiles.length > 1 && (
          <div className="glass-card p-4 rounded-xl border border-white/10">
            <p className="text-sm text-viraly-muted mb-2">Auto-Pilot will be configured for:</p>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-viraly-orange/20 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-viraly-orange" />
              </div>
              <span className="text-viraly-light font-medium">{activeProfile?.name}</span>
              <span className="text-xs px-2 py-0.5 bg-viraly-orange/20 text-viraly-orange rounded-full">Active</span>
            </div>
            <p className="text-xs text-viraly-muted mt-2">
              Switch profiles from the top navigation to set up Auto-Pilot for other brands.
            </p>
          </div>
        )}
        
        {/* Preview Posts - Compare Real Photos vs AI Generated */}
        <div className="glass-card p-6 rounded-xl border border-purple-500/30 bg-purple-500/5">
          <h3 className="text-lg font-semibold text-viraly-light mb-2 flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-400" />
            Preview: What Your Posts Could Look Like
          </h3>
          <p className="text-sm text-viraly-muted mb-4">
            Based on <span className="text-viraly-orange font-medium">{activeProfile?.name}</span>, here's a preview of content Viraly will create:
          </p>
          
          {/* Toggle between Real and AI */}
          <div className="flex items-center justify-center gap-2 mb-6 p-1 bg-white/5 rounded-lg w-fit mx-auto">
            <button
              onClick={() => setPreviewMode("real")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                previewMode === "real" 
                  ? 'bg-blue-500 text-white' 
                  : 'text-viraly-muted hover:text-viraly-light'
              }`}
            >
              Real Photos
            </button>
            <button
              onClick={() => setPreviewMode("ai")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                previewMode === "ai" 
                  ? 'bg-purple-500 text-white' 
                  : 'text-viraly-muted hover:text-viraly-light'
              }`}
            >
              AI Generated
            </button>
            <button
              onClick={() => setPreviewMode("compare")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                previewMode === "compare" 
                  ? 'bg-viraly-orange text-white' 
                  : 'text-viraly-muted hover:text-viraly-light'
              }`}
            >
              Compare
            </button>
          </div>
          
          {loadingPreview ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
              <span className="ml-2 text-viraly-muted">Generating preview...</span>
            </div>
          ) : previewMode === "compare" ? (
            /* Side-by-side comparison view - single post */
            <div className="space-y-6">
              {previewPosts.slice(0, 1).map((post, idx) => (
                <div key={post.id} className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full capitalize flex items-center gap-1">
                      {post.platform === 'instagram' && <Instagram className="w-3 h-3" />}
                      {post.platform === 'facebook' && <Facebook className="w-3 h-3" />}
                      {post.platform}
                    </span>
                    <span className="text-xs text-viraly-muted">Post {idx + 1}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    {/* Real Photo */}
                    <div className="space-y-2">
                      <div className="aspect-square rounded-xl overflow-hidden shadow-lg border-2 border-blue-500/30">
                        <img 
                          src={post.image_url} 
                          alt="Real Photo" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-blue-400 font-medium">Real Photo</p>
                      </div>
                    </div>
                    
                    {/* AI Generated */}
                    <div className="space-y-2">
                      <div className="aspect-square rounded-xl overflow-hidden shadow-lg border-2 border-purple-500/30 relative">
                        {post.ai_image_url ? (
                          <img 
                            src={post.ai_image_url} 
                            alt="AI Generated" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                            <div className="text-center">
                              <Sparkles className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                              <p className="text-xs text-purple-400">AI Preview</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-purple-400 font-medium">AI Generated</p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-viraly-light">{post.caption}</p>
                </div>
              ))}
              
              <div className="text-center p-4 bg-viraly-orange/10 border border-viraly-orange/30 rounded-xl">
                <p className="text-sm text-viraly-orange font-medium">
                  Can you spot the difference? AI-generated images are virtually indistinguishable!
                </p>
                <p className="text-xs text-viraly-muted mt-2">
                  Note: Photo credits shown here are for preview only. Your actual posts will NOT include any attribution.
                </p>
              </div>
            </div>
          ) : previewPosts.length > 0 ? (
            /* Single view (Real or AI) */
            <div className="space-y-4">
              {previewPosts.map((post, idx) => (
                <div key={post.id} className="flex gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:border-purple-500/30 transition-all">
                  <div className="w-28 h-28 rounded-xl overflow-hidden flex-shrink-0 shadow-lg">
                    <img 
                      src={previewMode === "ai" && post.ai_image_url ? post.ai_image_url : post.image_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full capitalize flex items-center gap-1">
                        {post.platform === 'instagram' && <Instagram className="w-3 h-3" />}
                        {post.platform === 'facebook' && <Facebook className="w-3 h-3" />}
                        {post.platform}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        previewMode === "ai" ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {previewMode === "ai" ? "AI Generated" : "Real Photo"}
                      </span>
                    </div>
                    <p className="text-sm text-viraly-light leading-relaxed">{post.caption}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-viraly-muted">
              <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Loading preview...</p>
            </div>
          )}
        </div>
        
        {/* Image Source Preference */}
        <div className="glass-card p-6 rounded-xl border border-white/10">
          <h3 className="text-lg font-semibold text-viraly-light mb-4 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-blue-400" />
            Which style do you prefer?
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={() => setConfig(prev => ({ ...prev, image_source: "real_photos" }))}
              className={`p-4 rounded-xl border text-left transition-all ${
                config.image_source === "real_photos"
                  ? 'bg-blue-500/10 border-blue-500/50 ring-2 ring-blue-500/30'
                  : 'bg-white/5 border-white/10 hover:border-white/30'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  config.image_source === "real_photos" ? 'border-blue-500 bg-blue-500' : 'border-white/30'
                }`}>
                  {config.image_source === "real_photos" && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="font-medium text-viraly-light">Real Photos</span>
              </div>
              <p className="text-xs text-viraly-muted">
                Professional stock photos - authentic, high-quality real images
              </p>
            </button>
            
            <button
              onClick={() => setConfig(prev => ({ ...prev, image_source: "ai_generated" }))}
              className={`p-4 rounded-xl border text-left transition-all ${
                config.image_source === "ai_generated"
                  ? 'bg-purple-500/10 border-purple-500/50 ring-2 ring-purple-500/30'
                  : 'bg-white/5 border-white/10 hover:border-white/30'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  config.image_source === "ai_generated" ? 'border-purple-500 bg-purple-500' : 'border-white/30'
                }`}>
                  {config.image_source === "ai_generated" && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="font-medium text-viraly-light">AI Generated</span>
              </div>
              <p className="text-xs text-viraly-muted">
                Unique AI-created visuals - stunning, one-of-a-kind images tailored to your brand
              </p>
            </button>
            
            {/* Smart Mix Option */}
            <button
              onClick={() => setConfig(prev => ({ ...prev, image_source: "smart_mix" }))}
              className={`p-4 rounded-xl border text-left transition-all ${
                config.image_source === "smart_mix"
                  ? 'bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-purple-500/50 ring-2 ring-purple-500/30'
                  : 'bg-white/5 border-white/10 hover:border-white/30'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  config.image_source === "smart_mix" ? 'border-purple-500 bg-gradient-to-r from-blue-500 to-purple-500' : 'border-white/30'
                }`}>
                  {config.image_source === "smart_mix" && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="font-medium text-viraly-light">Smart Mix</span>
                <span className="text-xs px-2 py-0.5 bg-viraly-orange/20 text-viraly-orange rounded-full">Recommended</span>
              </div>
              <p className="text-xs text-viraly-muted">
                Best of both worlds - alternates between real photos and AI-generated for varied, engaging content
              </p>
            </button>
          </div>
          
          {/* Smart Image Mode - Compact toggle, only for AI Generated or Smart Mix */}
          {(config.image_source === "ai_generated" || config.image_source === "smart_mix") && (
            <div className="mt-4 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-viraly-light">AI Face Mode</span>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setConfig(prev => ({ ...prev, smart_image_mode: "auto" }))}
                    className={`px-2 py-1 text-xs rounded transition-all ${
                      config.smart_image_mode === "auto"
                        ? "bg-purple-500 text-white"
                        : "bg-white/5 text-viraly-muted hover:bg-white/10"
                    }`}
                    title="AI automatically detects if faces should be avoided"
                  >
                    Auto
                  </button>
                  <button
                    onClick={() => setConfig(prev => ({ ...prev, smart_image_mode: "abstract" }))}
                    className={`px-2 py-1 text-xs rounded transition-all ${
                      config.smart_image_mode === "abstract"
                        ? "bg-purple-500 text-white"
                        : "bg-white/5 text-viraly-muted hover:bg-white/10"
                    }`}
                    title="Always use abstract imagery without faces"
                  >
                    No Faces
                  </button>
                  <button
                    onClick={() => setConfig(prev => ({ ...prev, smart_image_mode: "lifestyle" }))}
                    className={`px-2 py-1 text-xs rounded transition-all ${
                      config.smart_image_mode === "lifestyle"
                        ? "bg-purple-500 text-white"
                        : "bg-white/5 text-viraly-muted hover:bg-white/10"
                    }`}
                    title="Allow lifestyle imagery with people"
                  >
                    Lifestyle
                  </button>
                </div>
              </div>
              <p className="text-[10px] text-viraly-muted mt-1">
                {config.smart_image_mode === "auto" && "AI decides based on your content"}
                {config.smart_image_mode === "abstract" && "Best for personal brands & services"}
                {config.smart_image_mode === "lifestyle" && "Includes natural people imagery"}
              </p>
            </div>
          )}
        </div>
        
        <Button 
          className="w-full btn-primary py-6 text-lg"
          onClick={() => setStep(2)}
          disabled={loading}
        >
          Let's Configure Auto-Pilot
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    );
  };
  
  // Render Step 2: Configure Output + Frequency + Platforms
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h2 className="text-xl font-bold text-viraly-light">Configure Your Auto-Pilot</h2>
      </div>
      
      {/* Picture Posts Section */}
      <div className="glass-card p-6 rounded-xl border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-viraly-light flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-blue-400" />
            Picture Posts
          </h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.picture_posts_enabled}
              onChange={(e) => setConfig(prev => ({ ...prev, picture_posts_enabled: e.target.checked }))}
              className="sr-only"
            />
            <div className={`w-12 h-6 rounded-full transition-colors ${config.picture_posts_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}>
              <div className={`w-5 h-5 rounded-full bg-white transition-transform mt-0.5 ${config.picture_posts_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </div>
          </label>
        </div>
        
        {config.picture_posts_enabled && (
          <div>
            <p className="text-sm text-viraly-muted mb-3">How many picture posts per week?</p>
            <div className="flex flex-wrap gap-2">
              {frequencyOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setConfig(prev => ({ ...prev, picture_posts_per_week: opt.value }))}
                  className={`px-4 py-2 rounded-lg border transition-all ${
                    config.picture_posts_per_week === opt.value
                      ? 'bg-viraly-orange/20 border-viraly-orange text-viraly-orange'
                      : 'bg-white/5 border-white/10 text-viraly-muted hover:border-white/30'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Video Posts Section */}
      <div className="glass-card p-6 rounded-xl border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-viraly-light flex items-center gap-2">
            <Video className="w-5 h-5 text-purple-400" />
            Video Posts
          </h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.video_posts_enabled}
              onChange={(e) => setConfig(prev => ({ ...prev, video_posts_enabled: e.target.checked }))}
              className="sr-only"
            />
            <div className={`w-12 h-6 rounded-full transition-colors ${config.video_posts_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}>
              <div className={`w-5 h-5 rounded-full bg-white transition-transform mt-0.5 ${config.video_posts_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </div>
          </label>
        </div>
        
        {config.video_posts_enabled && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-viraly-muted mb-3">How many video posts per week?</p>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3].map(val => (
                  <button
                    key={val}
                    onClick={() => setConfig(prev => ({ ...prev, video_posts_per_week: val }))}
                    className={`px-4 py-2 rounded-lg border transition-all ${
                      config.video_posts_per_week === val
                        ? 'bg-viraly-orange/20 border-viraly-orange text-viraly-orange'
                        : 'bg-white/5 border-white/10 text-viraly-muted hover:border-white/30'
                    }`}
                  >
                    {val}/week
                  </button>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-viraly-muted mb-2">Duration</p>
                <select
                  value={config.video_duration}
                  onChange={(e) => setConfig(prev => ({ ...prev, video_duration: parseInt(e.target.value) }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-viraly-light"
                >
                  <option value={10}>10 seconds</option>
                  <option value={20}>20 seconds</option>
                  <option value={30}>30 seconds (PAYG)</option>
                </select>
              </div>
              <div>
                <p className="text-sm text-viraly-muted mb-2">Resolution</p>
                <select
                  value={config.video_resolution}
                  onChange={(e) => setConfig(prev => ({ ...prev, video_resolution: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-viraly-light"
                >
                  <option value="720p">720p HD</option>
                  <option value="1080p">1080p Full HD</option>
                </select>
              </div>
            </div>
            
            <p className="text-xs text-viraly-muted">
              Your Plan: {user?.current_plan || "Free"} - 
              {user?.current_plan === "growth" ? " 10s-20s at 720p" : 
               user?.current_plan === "pro" ? " 20s at 720p or 1080p" : " Videos require Growth or Pro plan"}
            </p>
          </div>
        )}
      </div>
      
      {/* Platforms Section */}
      <div className="glass-card p-6 rounded-xl border border-white/10">
        <h3 className="text-lg font-semibold text-viraly-light mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-green-400" />
          Platforms
        </h3>
        
        <p className="text-sm text-viraly-muted mb-3">Where should we post?</p>
        
        <div className="grid grid-cols-2 gap-3">
          {["instagram", "facebook", "tiktok", "pinterest"].map(platform => {
            const isConnected = socialAccounts.some(a => a.platform === platform && a.is_connected);
            const isSelected = config.platforms.includes(platform);
            
            return (
              <button
                key={platform}
                onClick={() => isConnected && togglePlatform(platform)}
                disabled={!isConnected}
                className={`p-4 rounded-xl border transition-all flex items-center gap-3 ${
                  isSelected
                    ? 'bg-viraly-orange/20 border-viraly-orange'
                    : isConnected
                      ? 'bg-white/5 border-white/10 hover:border-white/30'
                      : 'bg-white/5 border-white/5 opacity-50 cursor-not-allowed'
                }`}
              >
                <PlatformIcon platform={platform} className="w-5 h-5" />
                <span className="capitalize text-viraly-light">{platform}</span>
                {isSelected && <Check className="w-4 h-4 text-viraly-orange ml-auto" />}
                {!isConnected && <span className="text-xs text-viraly-muted ml-auto">Not connected</span>}
              </button>
            );
          })}
        </div>
        
        {/* Only show Connect more platforms if not all 4 are connected */}
        {socialAccounts.filter(a => a.is_connected).length < 4 && (
          <Button variant="ghost" size="sm" className="mt-3 text-viraly-orange" onClick={() => window.location.href = "/accounts"}>
            <Plus className="w-4 h-4 mr-1" /> Connect more platforms
          </Button>
        )}
      </div>
      
      {/* Platform Permissions Checklist - One-time consent for TikTok/Pinterest */}
      {needsPlatformPermissions() && (
        <PlatformPermissionsChecklist
          selectedPlatforms={config.platforms}
          onPermissionsComplete={handlePlatformPermissionsComplete}
          compact={true}
          mode="autopilot"
        />
      )}
      
      {/* Credit Estimate Section */}
      {creditEstimate && (
        <div className="glass-card p-6 rounded-xl border border-white/10">
          <h3 className="text-lg font-semibold text-viraly-light mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-yellow-400" />
            Credit Estimate
          </h3>
          
          <div className="space-y-3">
            <p className="text-sm text-viraly-muted">Monthly usage based on your selections:</p>
            
            <div className="bg-white/5 rounded-lg p-4 space-y-2">
              {config.picture_posts_enabled && config.picture_posts_per_week > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-viraly-muted">{creditEstimate.total_picture_posts || 0} picture posts</span>
                  <span className="text-viraly-light">{creditEstimate.picture_credits || 0} credits</span>
                </div>
              )}
              {config.video_posts_enabled && config.video_posts_per_week > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-viraly-muted">{creditEstimate.total_video_posts || 0} videos ({config.video_duration}s {config.video_resolution})</span>
                  <span className="text-viraly-light">{creditEstimate.video_credits || 0} credits</span>
                </div>
              )}
            </div>
            
            {/* Available vs Needed */}
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-viraly-muted">Picture credits available:</span>
                <span className={(creditEstimate.available_picture_credits || 0) >= (creditEstimate.picture_credits || 0) ? 'text-viraly-success' : 'text-yellow-500'}>
                  {creditEstimate.available_picture_credits || 0}
                </span>
              </div>
              
              {config.video_posts_enabled && (
                <div className="flex justify-between text-sm">
                  <span className="text-viraly-muted">Video credits available:</span>
                  <span className={(creditEstimate.available_video_credits || 0) >= (creditEstimate.video_credits || 0) ? 'text-viraly-success' : 'text-yellow-500'}>
                    {creditEstimate.available_video_credits || 0}
                  </span>
                </div>
              )}
            </div>
            
            {/* Suggestions if doesn't fit */}
            {!creditEstimate.fits_plan && creditEstimate.suggestions?.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm font-medium text-yellow-400 mb-3">Options to Fit Your Plan:</p>
                
                <div className="space-y-3">
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-viraly-muted mb-2">Option A: Adjust your settings</p>
                    {creditEstimate.suggestions.map((suggestion, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm text-viraly-light">{suggestion.message}</span>
                        <Button size="sm" variant="outline" onClick={() => applyAdjustment(suggestion)}>
                          Apply
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-viraly-muted mb-2">Option B: Get more credits</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => window.location.href = "/pricing"}>
                        Buy Credit Pack
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => window.location.href = "/pricing"}>
                        Upgrade Plan
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {creditEstimate.fits_plan && (
              <div className="flex items-center gap-2 text-viraly-success text-sm mt-2">
                <Check className="w-4 h-4" />
                Your plan covers this configuration!
              </div>
            )}
          </div>
        </div>
      )}
      
      <Button 
        className="w-full btn-primary py-6 text-lg"
        onClick={() => setStep(3)}
        disabled={config.platforms.length === 0 || (!config.picture_posts_enabled && !config.video_posts_enabled) || needsPlatformPermissions()}
      >
        {needsPlatformPermissions() ? (
          <>Complete platform permissions above</>
        ) : (
          <>
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
  
  // Render Step 3: Approval Mode
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h2 className="text-xl font-bold text-viraly-light">Approval Mode</h2>
      </div>
      
      <div className="space-y-4">
        {/* Review First Option */}
        <button
          onClick={() => setConfig(prev => ({ ...prev, approval_mode: "review_first" }))}
          className={`w-full p-6 rounded-xl border transition-all text-left ${
            config.approval_mode === "review_first"
              ? 'bg-viraly-orange/20 border-viraly-orange'
              : 'bg-white/5 border-white/10 hover:border-white/30'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              config.approval_mode === "review_first" ? 'border-viraly-orange bg-viraly-orange' : 'border-white/30'
            }`}>
              {config.approval_mode === "review_first" && <Check className="w-4 h-4 text-white" />}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-viraly-light mb-1 flex items-center gap-2">
                Review First
                <span className="text-xs px-2 py-0.5 bg-viraly-orange/20 text-viraly-orange rounded-full">Recommended</span>
              </h3>
              <p className="text-sm text-viraly-muted">
                You'll review and approve content before it's posted. Switch to Auto-Approve anytime once you're confident.
              </p>
            </div>
          </div>
        </button>
        
        {/* Auto-Approve Option */}
        <button
          onClick={() => setConfig(prev => ({ ...prev, approval_mode: "auto_approve" }))}
          className={`w-full p-6 rounded-xl border transition-all text-left ${
            config.approval_mode === "auto_approve"
              ? 'bg-viraly-orange/20 border-viraly-orange'
              : 'bg-white/5 border-white/10 hover:border-white/30'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              config.approval_mode === "auto_approve" ? 'border-viraly-orange bg-viraly-orange' : 'border-white/30'
            }`}>
              {config.approval_mode === "auto_approve" && <Check className="w-4 h-4 text-white" />}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-viraly-light mb-1">Auto-Approve</h3>
              <p className="text-sm text-viraly-muted">
                Posts go live automatically at optimal times. You can still edit before posting time.
              </p>
            </div>
          </div>
        </button>
      </div>
      
      {/* Batch Frequency - Only show if Review First */}
      {config.approval_mode === "review_first" && (
        <div className="glass-card p-6 rounded-xl border border-white/10">
          <h3 className="text-lg font-semibold text-viraly-light mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            How Often Do You Want to Review?
          </h3>
          
          <div className="space-y-3">
            {[
              { value: "monthly", label: "Monthly Batch", desc: "Create entire month's content at once. Review in one session.", recommended: true },
              { value: "weekly", label: "Weekly Batch", desc: "Create 7 days of content each Sunday. Review once a week." },
              { value: "daily", label: "Daily", desc: "Create each post 24 hours before. You approve daily." }
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setConfig(prev => ({ ...prev, batch_frequency: opt.value }))}
                className={`w-full p-4 rounded-lg border transition-all text-left ${
                  config.batch_frequency === opt.value
                    ? 'bg-viraly-orange/10 border-viraly-orange/50'
                    : 'bg-white/5 border-white/10 hover:border-white/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    config.batch_frequency === opt.value ? 'border-viraly-orange bg-viraly-orange' : 'border-white/30'
                  }`}>
                    {config.batch_frequency === opt.value && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-viraly-light flex items-center gap-2">
                      {opt.label}
                      {opt.recommended && (
                        <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">Recommended</span>
                      )}
                    </span>
                    <p className="text-xs text-viraly-muted">{opt.desc}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      
      <Button 
        className="w-full btn-primary py-6 text-lg"
        onClick={() => setStep(4)}
      >
        Continue
        <ChevronRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );
  
  // Render Step 4: Auto-Charge Options
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => setStep(3)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h2 className="text-xl font-bold text-viraly-light">Stay Uninterrupted</h2>
      </div>
      
      <div className="glass-card p-6 rounded-xl border border-white/10 space-y-4">
        <div className="flex items-center gap-2 text-viraly-muted mb-4">
          <Info className="w-4 h-4" />
          <span className="text-sm">Optional: Keep Auto-Pilot running without interruption</span>
        </div>
        
        {/* Auto-Renewal Toggle */}
        <label className="flex items-start gap-4 p-4 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
          <input
            type="checkbox"
            checked={config.auto_renewal_enabled}
            onChange={(e) => setConfig(prev => ({ ...prev, auto_renewal_enabled: e.target.checked }))}
            className="sr-only"
          />
          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
            config.auto_renewal_enabled ? 'bg-viraly-orange border-viraly-orange' : 'border-white/30'
          }`}>
            {config.auto_renewal_enabled && <Check className="w-4 h-4 text-white" />}
          </div>
          <div className="flex-1">
            <span className="font-medium text-viraly-light">Enable Auto-Renewal</span>
            <p className="text-xs text-viraly-muted">Automatically renew your plan so you never run out of credits.</p>
          </div>
        </label>
        
        {/* Auto Top-Up Toggle */}
        <label className="flex items-start gap-4 p-4 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
          <input
            type="checkbox"
            checked={config.auto_topup_enabled}
            onChange={(e) => setConfig(prev => ({ ...prev, auto_topup_enabled: e.target.checked }))}
            className="sr-only"
          />
          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
            config.auto_topup_enabled ? 'bg-viraly-orange border-viraly-orange' : 'border-white/30'
          }`}>
            {config.auto_topup_enabled && <Check className="w-4 h-4 text-white" />}
          </div>
          <div className="flex-1">
            <span className="font-medium text-viraly-light">Enable Auto Top-Up When Low</span>
            <p className="text-xs text-viraly-muted mt-1">
              If credits run low, auto-purchase a small bundle.
            </p>
            
            {/* Max amount selector - always visible when checkbox is checked */}
            {config.auto_topup_enabled ? (
              <div className="mt-3 flex items-center gap-2">
                <label className="text-sm text-viraly-muted">Max per month:</label>
                <select
                  value={config.auto_topup_max_amount}
                  onChange={(e) => setConfig(prev => ({ ...prev, auto_topup_max_amount: parseInt(e.target.value) }))}
                  className="bg-viraly-orange/20 border border-viraly-orange rounded-lg px-3 py-2 text-sm text-viraly-light font-medium cursor-pointer hover:bg-viraly-orange/30 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value={10}>$10</option>
                  <option value={25}>$25</option>
                  <option value={50}>$50</option>
                  <option value={100}>$100</option>
                  <option value={150}>$150</option>
                  <option value={200}>$200</option>
                </select>
              </div>
            ) : (
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConfig(prev => ({ ...prev, auto_topup_enabled: true }));
                }}
                className="mt-2 inline-flex items-center gap-1 text-sm text-viraly-orange hover:text-viraly-orange/80 underline cursor-pointer"
              >
                Click to set your limit (up to $200/month)
              </button>
            )}
          </div>
        </label>
        
        {/* Warning message */}
        {!config.auto_renewal_enabled && !config.auto_topup_enabled && (
          <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-200">
              Without these options, Auto-Pilot may pause if credits run out. You'll receive push notifications to top up.
            </p>
          </div>
        )}
        
        {/* Card on file notice */}
        <p className="text-xs text-viraly-muted flex items-center gap-2 pt-2">
          <CreditCard className="w-4 h-4" />
          Auto-subscription requires a card on file. 
          {!user?.has_payment_method && (
            <Button 
              variant="link" 
              size="sm" 
              className="text-viraly-orange p-0 h-auto underline"
              onClick={() => setShowPaymentModal(true)}
            >
              Add Payment Method →
            </Button>
          )}
        </p>
        
        {/* Payment Method Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowPaymentModal(false)}>
            <div className="bg-[#1a1a2e] rounded-xl p-6 max-w-md w-full border border-white/10" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-viraly-light mb-4 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-viraly-orange" />
                Add Payment Method
              </h3>
              <p className="text-viraly-muted mb-4">
                To enable auto top-up and auto-renewal, you need a payment method on file.
              </p>
              <p className="text-sm text-viraly-muted mb-6">
                Your payment details will be securely saved via PayPal when you purchase any plan or credit pack.
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowPaymentModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 btn-primary"
                  onClick={() => {
                    setShowPaymentModal(false);
                    navigate('/pricing');
                  }}
                >
                  Go to Pricing
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <Button 
        className="w-full btn-primary py-6 text-lg"
        onClick={() => setStep(5)}
      >
        Review Content Plan
        <ChevronRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );
  
  // Render Step 5: Review Content Plan & Activate
  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => setStep(4)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h2 className="text-xl font-bold text-viraly-light">Your Content Plan</h2>
      </div>
      
      <div className="glass-card p-6 rounded-xl border border-white/10">
        <p className="text-sm text-viraly-muted mb-4">
          Based on your brand profile and location, here's your personalized content plan:
        </p>
        
        {contentPlan?.year_plan ? (
          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
            {contentPlan.year_plan.slice(0, showFullYear ? 52 : 12).map((week, idx) => (
              <div key={idx} className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-white/20 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-viraly-light">Week {week.week_number}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => generateTextPreview(week)}
                      className="text-xs px-2 py-1 bg-viraly-orange/20 text-viraly-orange rounded hover:bg-viraly-orange/30 transition-colors"
                    >
                      Preview
                    </button>
                    <span className="text-xs text-viraly-muted">{week.week_start} - {week.week_end}</span>
                  </div>
                </div>
                <p className="text-viraly-orange font-medium">{week.theme}</p>
                <p className="text-sm text-viraly-muted mt-1">{week.content_angle}</p>
                {week.events?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {week.events.slice(0, 3).map((event, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">
                        {event}
                      </span>
                    ))}
                  </div>
                )}
                {week.location_context && (
                  <p className="text-xs text-green-400 mt-1">📍 {week.location_context}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
            <span className="ml-2 text-viraly-muted">Generating your content plan...</span>
          </div>
        )}
        
        {/* Text-Only Content Preview Modal */}
        {previewWeek && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewWeek(null)}>
            <div className="bg-[#1a1a2e] rounded-xl p-6 max-w-lg w-full border border-white/10 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-viraly-light">
                  Week {previewWeek.week_number} Preview
                </h3>
                <button onClick={() => setPreviewWeek(null)} className="text-viraly-muted hover:text-white">✕</button>
              </div>
              
              <div className="mb-4 p-3 bg-white/5 rounded-lg">
                <p className="text-sm text-viraly-orange font-medium mb-1">{previewWeek.theme}</p>
                <p className="text-xs text-viraly-muted">{previewWeek.content_angle}</p>
              </div>
              
              {loadingPreview ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                  <span className="ml-2 text-viraly-muted">Generating preview...</span>
                </div>
              ) : previewContent ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-viraly-muted uppercase tracking-wider">Sample Caption</label>
                    <div className="mt-1 p-3 bg-white/5 rounded-lg text-sm text-viraly-light whitespace-pre-wrap">
                      {previewContent.caption}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-viraly-muted uppercase tracking-wider">Suggested Hashtags</label>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(previewContent.hashtags || []).map((tag, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-viraly-muted uppercase tracking-wider">Call to Action</label>
                    <p className="mt-1 text-sm text-viraly-success">{previewContent.cta}</p>
                  </div>
                  
                  {previewContent.platform_tips && (
                    <div>
                      <label className="text-xs text-viraly-muted uppercase tracking-wider">Platform Tips</label>
                      <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
                        {Object.entries(previewContent.platform_tips).map(([platform, tip]) => (
                          <div key={platform} className="p-2 bg-white/5 rounded">
                            <span className="text-viraly-orange capitalize">{platform}:</span>
                            <span className="text-viraly-muted ml-1">{tip}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-viraly-muted italic mt-4">
                    * This is a text-only preview. Actual posts will include AI-generated images tailored to your brand.
                  </p>
                </div>
              ) : null}
              
              <Button 
                className="w-full mt-4 btn-primary"
                onClick={() => setPreviewWeek(null)}
              >
                Close Preview
              </Button>
            </div>
          </div>
        )}
        
        <div className="flex gap-2 mt-4">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowFullYear(!showFullYear)}
          >
            {showFullYear ? 'Show First 12 Weeks' : 'View Full Year (52 Weeks)'}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={async () => {
              try {
                // Regenerate entire year plan
                await apiClient.post("/playbook/reshuffle-week", { week_number: 0, regenerate_all: true });
                // Refetch content plan
                const res = await apiClient.get("/playbook/content-plan");
                setContentPlan(res.data);
                toast.success("Content plan regenerated with fresh themes!");
              } catch (err) {
                toast.error("Failed to reshuffle");
              }
            }}
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Reshuffle
          </Button>
        </div>
      </div>
      
      {/* Summary Card */}
      <div className="glass-card p-6 rounded-xl border border-viraly-orange/30 bg-viraly-orange/5">
        <h3 className="font-semibold text-viraly-light mb-4">Configuration Summary</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-viraly-muted">Picture Posts:</span>
            <span className="ml-2 text-viraly-light">
              {config.picture_posts_enabled ? `${config.picture_posts_per_week}/week` : "Disabled"}
            </span>
          </div>
          <div>
            <span className="text-viraly-muted">Video Posts:</span>
            <span className="ml-2 text-viraly-light">
              {config.video_posts_enabled ? `${config.video_posts_per_week}/week` : "Disabled"}
            </span>
          </div>
          <div>
            <span className="text-viraly-muted">Platforms:</span>
            <span className="ml-2 text-viraly-light capitalize">
              {config.platforms.join(", ") || "None selected"}
            </span>
          </div>
          <div>
            <span className="text-viraly-muted">Approval:</span>
            <span className="ml-2 text-viraly-light">
              {config.approval_mode === "auto_approve" ? "Auto-Approve" : "Review First"}
            </span>
          </div>
          <div>
            <span className="text-viraly-muted">Image Style:</span>
            <span className="ml-2 text-viraly-light">
              {config.image_source === "ai_generated" ? "AI Generated" : 
               config.image_source === "smart_mix" ? "Smart Mix (AI + Real)" : "Real Photos"}
            </span>
          </div>
        </div>
      </div>
      
      <Button 
        className="w-full btn-primary py-6 text-lg"
        onClick={activateAutoPilot}
        disabled={savingConfig}
      >
        {savingConfig ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Activating...
          </>
        ) : (
          <>
            <Zap className="w-5 h-5 mr-2" />
            Activate Auto-Pilot
          </>
        )}
      </Button>
    </div>
  );
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
      </div>
    );
  }
  
  return (
    <div className="max-w-xl mx-auto">
      {/* Step indicators */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {[1, 2, 3, 4, 5].map(s => (
          <div
            key={s}
            className={`w-2 h-2 rounded-full transition-all ${
              s === step ? 'w-8 bg-viraly-orange' : s < step ? 'bg-viraly-orange' : 'bg-white/20'
            }`}
          />
        ))}
      </div>
      
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
